// Excel de Pedidos de venta.
//
// Antes era una única hoja plana con cabecera y líneas indentadas. Perdía todo lo
// que la pantalla enseña agrupado —qué se facturó en cada factura, qué queda
// pendiente, qué hay preparado en cada carga— y obligaba a rehacer esas cuentas a
// mano en Excel.
//
// Ahora son cinco hojas planas, una por tipo de dato, todas con el número de
// pedido delante: cada una se filtra y se cruza con una tabla dinámica sin tocar
// nada. Se descartó la hoja única «como la pantalla» porque lo que se gana en
// parecido se pierde en poder trabajar el dato, que es para lo que se baja un
// Excel.
//
// Se usa ExcelJS y no SheetJS —como en los exports de reports, secuencia y
// producción diaria— porque hacen falta a la vez estilo de celda, formato
// numérico de verdad y fechas como fecha en lugar de texto.
//
// La fila de totales usa SUBTOTAL(9) y no SUMA: así el total sigue al filtro que
// ponga el usuario en vez de quedarse contando lo que ya no se ve.
import ExcelJS from 'exceljs'
import type { TFunction } from 'i18next'
import type { AlbaranLinea, CargaLinea, PedidoVentaLinea, PedidoVentaRow } from '@/types'
import { agruparPorTransid, estadoDe, pendienteDeLanzar } from '@/utils/cargasPicking'
import { agruparAlbaranes, albaranesDeFila } from '@/utils/albaranesLinea'

// Paleta compartida con los demás exports del repo.
const HEADER = { bg: 'FF0284C7', font: 'FFFFFFFF' }   // sky-600
const BAND = 'FFF8FAFC'                                // slate-50
const BORDER = 'FFE2E8F0'                              // slate-200
const TOTAL = { bg: 'FFE0F2FE', font: 'FF0369A1' }    // sky-100 / sky-700

const FMT_CANT = '#,##0'
const FMT_IMP = '#,##0.00'
const FMT_FECHA = 'dd/mm/yyyy'

type Tipo = 'texto' | 'cantidad' | 'importe' | 'fecha' | 'entero'

interface Col<T> {
  /** Rótulo de la cabecera. Ya traducido. */
  titulo: string
  tipo?: Tipo
  ancho?: number
  /** Valor de la celda. `undefined`/`null` deja la celda VACÍA, nunca a cero. */
  valor: (fila: T) => string | number | Date | null | undefined
  /** Entra en la fila de totales. Sólo para columnas aditivas. */
  total?: boolean
}

function fill(argb: string): ExcelJS.Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } }
}

function borde(): ExcelJS.Borders {
  const lado = { style: 'thin' as const, color: { argb: BORDER } }
  return { top: lado, left: lado, bottom: lado, right: lado } as ExcelJS.Borders
}

const FORMATO: Record<Tipo, string | undefined> = {
  texto: undefined,
  entero: '#,##0',
  cantidad: FMT_CANT,
  importe: FMT_IMP,
  fecha: FMT_FECHA,
}

/** Convierte una fecha ISO en Date para que Excel la ordene y filtre como fecha. */
function fecha(iso?: string | null): Date | null {
  if (!iso) return null
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Escribe una hoja: cabecera fija, autofiltro, bandas alternas y fila de totales.
 *
 * Devuelve la hoja para poder rematarla desde fuera si hiciera falta.
 */
function hoja<T>(
  wb: ExcelJS.Workbook,
  nombre: string,
  cols: Col<T>[],
  filas: T[],
  etiquetaTotal: string,
): ExcelJS.Worksheet {
  // Excel no admite algunos caracteres en el nombre de la pestaña y corta a 31.
  const ws = wb.addWorksheet(nombre.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31), {
    views: [{ state: 'frozen', ySplit: 1 }],
  })

  ws.columns = cols.map(c => ({ width: c.ancho ?? 14 }))

  const cabecera = ws.addRow(cols.map(c => c.titulo))
  cabecera.height = 20
  cabecera.eachCell(celda => {
    celda.fill = fill(HEADER.bg)
    celda.font = { bold: true, color: { argb: HEADER.font }, size: 10 }
    celda.alignment = { vertical: 'middle', horizontal: 'left' }
    celda.border = borde()
  })

  filas.forEach((f, i) => {
    const fila = ws.addRow(cols.map(c => c.valor(f) ?? null))
    fila.eachCell({ includeEmpty: true }, (celda, n) => {
      const col = cols[n - 1]
      if (!col) return
      celda.font = { size: 10 }
      celda.border = borde()
      const fmt = FORMATO[col.tipo ?? 'texto']
      if (fmt) celda.numFmt = fmt
      if (col.tipo && col.tipo !== 'texto' && col.tipo !== 'fecha') {
        celda.alignment = { horizontal: 'right' }
      }
      // Bandas alternas: se leen mejor las filas largas, y son muchas columnas.
      if (i % 2 === 1) celda.fill = fill(BAND)
    })
  })

  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: cols.length },
  }

  if (filas.length > 0 && cols.some(c => c.total)) {
    const primera = 2
    const ultima = filas.length + 1
    const total = ws.addRow(cols.map((c, i) => {
      if (i === 0) return etiquetaTotal
      if (!c.total) return null
      const letra = ws.getColumn(i + 1).letter
      // SUBTOTAL(9) y no SUM: el total sigue al autofiltro.
      return { formula: `SUBTOTAL(9,${letra}${primera}:${letra}${ultima})` }
    }))
    total.eachCell({ includeEmpty: true }, (celda, n) => {
      const col = cols[n - 1]
      celda.fill = fill(TOTAL.bg)
      celda.font = { bold: true, color: { argb: TOTAL.font }, size: 10 }
      celda.border = borde()
      const fmt = FORMATO[col?.tipo ?? 'texto']
      if (fmt && col?.total) celda.numFmt = fmt
      if (col?.total) celda.alignment = { horizontal: 'right' }
    })
  }

  return ws
}

export interface DatosExportVentas {
  pedidos: PedidoVentaRow[]
  lineas: PedidoVentaLinea[]
  cargas: CargaLinea[]
  albaranes: AlbaranLinea[]
}

interface Opciones {
  isEmpresaAll: boolean
  t: TFunction
  /** Formateo del código de artículo según rol (useFmtArticulo). */
  fmtArt: (a?: string) => string
  generado?: Date
}

/** Construye el libro. Puro: no descarga nada, así se puede probar. */
export function buildPedidosVentasWorkbook(
  { pedidos, lineas, cargas, albaranes }: DatosExportVentas,
  { isEmpresaAll, t, fmtArt, generado = new Date() }: Opciones,
): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'Airval Planning'
  wb.created = generado
  wb.title = String(t('salesOrders.title'))

  const x = (k: string) => String(t(`salesOrders.excel.${k}`))

  /** El estado de un pedido o de una línea, traducido.
   *
   *  El backend lo emite YA EN CASTELLANO: `SALES_STATUS` convierte el entero de
   *  D365 a texto antes de salir, así que lo que llega es «Pedido abierto», no un
   *  1. La pantalla lo traduce al pintarlo; el Excel lo escribía crudo, y el
   *  fichero en inglés salía con dos columnas en español.
   *
   *  Las claves i18n van indexadas POR EL VALOR castellano —`salesOrders.status`
   *  tiene «Pedido abierto» como clave—, así que renombrar uno de esos textos en
   *  el backend obliga a renombrar su clave aquí. El `defaultValue` evita que un
   *  estado nuevo salga como una clave a medio resolver: sale tal cual vino. */
  const estado = (v?: string | null) =>
    (v ? String(t(`salesOrders.status.${v}`, { defaultValue: v })) : null)

  // Índices para no recorrer las listas dentro de cada fila.
  const pickingPorTransid = agruparPorTransid(cargas)
  const porPedido = new Map<string, PedidoVentaLinea[]>()
  for (const l of lineas) {
    const arr = porPedido.get(l.numPedido)
    if (arr) arr.push(l); else porPedido.set(l.numPedido, [l])
  }
  const cargasPorPedido = new Map<string, Set<string>>()
  for (const c of cargas) {
    const set = cargasPorPedido.get(c.numPedido)
    if (set) set.add(c.carga); else cargasPorPedido.set(c.numPedido, new Set([c.carga]))
  }
  const lineaPorTransid = new Map<string, PedidoVentaLinea>()
  for (const l of lineas) if (l.inventtransid) lineaPorTransid.set(l.inventtransid, l)

  // El código de cliente sólo vive en la cabecera, y hasta ahora sólo salía en la
  // hoja de pedidos. El cliente pidió tenerlo en las demás para poder usar cada
  // hoja por separado sin cruzarla a mano.
  const clientePorPedido = new Map<string, string>()
  for (const p of pedidos) clientePorPedido.set(p.salesid, p.custaccount)

  // Los albaranes usan la MISMA función que la pantalla, no una copia. Que el
  // Excel y la pantalla dijeran cosas distintas de la misma magnitud es la queja
  // que trajo esta columna; hay un test que compara las dos salidas.
  const indiceAlbaranes = agruparAlbaranes(albaranes)
  const albaranDeFila = (l: PedidoVentaLinea) =>
    albaranesDeFila(indiceAlbaranes, l)?.albaranes.join(', ') || null

  // La carga en la que salió ESA entrega, que sale del albarán. No se cruza con
  // `cargas`: esa lista son todas las cargas de la línea —hasta 19 en un pedido
  // medido— y no sabe cuál corresponde a qué factura. Vacío significa que no
  // salió por una carga, no que se desconozca.
  const cargaDeFila = (l: PedidoVentaLinea) =>
    albaranesDeFila(indiceAlbaranes, l)?.cargas.join(', ') || null

  // Destino y referencia son atributos de la CARGA, constantes dentro de ella.
  // Van cruzados respecto a su nombre en origen: `referenciaCliente` es
  // `cargacliente_custom`, que no es un cliente sino el destino.
  const refsPorCarga = new Map<string, { destino?: string; referencia?: string }>()
  for (const c of cargas) {
    if (!refsPorCarga.has(c.carga)) {
      refsPorCarga.set(c.carga, {
        destino: c.referenciaCliente?.trim() || undefined,
        referencia: c.referenciaCliente2?.trim() || undefined,
      })
    }
  }
  // TODAS las cargas de la fila, no la primera: una factura puede cubrir dos
  // envíos, y enseñar el destino de uno como si fuera el de la fila es un dato
  // correcto en el sitio equivocado. Mismo criterio que la pantalla.
  const refDeFila = (l: PedidoVentaLinea, campo: 'destino' | 'referencia') => {
    const cargas = albaranesDeFila(indiceAlbaranes, l)?.cargas ?? []
    const valores = [...new Set(
      cargas.map(c => refsPorCarga.get(c)?.[campo]).filter(Boolean) as string[],
    )]
    return valores.join(' · ') || null
  }

  // Lo que aún no se ha lanzado a ninguna carga: mismo cálculo que la pantalla.
  const sinLanzar = (l: PedidoVentaLinea) => pendienteDeLanzar(
    l.qtyLinea, l.inventtransid ? pickingPorTransid.get(l.inventtransid) : undefined,
  ).pendiente

  const colEmpresa = <T,>(valor: (f: T) => string): Col<T>[] =>
    isEmpresaAll ? [{ titulo: x('company'), ancho: 10, valor }] : []

  /** Código de cliente derivado del nº de pedido. Va DETRÁS de `orderId`, nunca
   *  en primera posición: la hoja se lee y se filtra por pedido. */
  const colCliente = <T,>(pedidoDe: (f: T) => string): Col<T> => ({
    titulo: x('customerCode'), ancho: 14,
    valor: f => clientePorPedido.get(pedidoDe(f)) ?? null,
  })

  // ── Hoja 1: un pedido por fila ────────────────────────────────────────────
  hoja<PedidoVentaRow>(wb, x('sheetOrders'), [
    ...colEmpresa<PedidoVentaRow>(r => r.dataareaid),
    { titulo: x('orderId'), ancho: 18, valor: r => r.salesid },
    { titulo: x('customer'), ancho: 30, valor: r => r.nombreCliente ?? r.custaccount },
    { titulo: x('customerCode'), ancho: 14, valor: r => r.custaccount },
    { titulo: x('customerRef'), ancho: 18, valor: r => r.customerRef ?? null },
    { titulo: x('customerGroup'), ancho: 14, valor: (r: PedidoVentaRow) => r.grupoCliente ?? '' },
    { titulo: x('orderDate'), tipo: 'fecha', valor: r => fecha(r.fechaPedido) },
    { titulo: x('deliveryDate'), tipo: 'fecha', valor: r => fecha(r.fechaEntrega) },
    { titulo: x('status'), ancho: 16, valor: r => estado(r.salesstatus) },
    { titulo: x('family'), ancho: 18, valor: r => r.familia ?? null },
    { titulo: x('qtyOrdered'), tipo: 'cantidad', total: true, valor: r => r.qtyPedida },
    { titulo: x('qtyServed'), tipo: 'cantidad', total: true, valor: r => r.qtyServida },
    { titulo: x('qtyPending'), tipo: 'cantidad', total: true, valor: r => r.qtyPendiente },
    { titulo: x('totalAmount'), tipo: 'importe', total: true, ancho: 16, valor: r => r.importeTotal },
    { titulo: x('pendingAmount'), tipo: 'importe', total: true, ancho: 16, valor: r => r.importePendiente },
    { titulo: x('currency'), ancho: 9, valor: r => r.moneda ?? 'EUR' },
    {
      titulo: x('lineCount'), tipo: 'entero', total: true, ancho: 10,
      valor: r => (porPedido.get(r.salesid) ?? []).length,
    },
    {
      titulo: x('invoiceCount'), tipo: 'entero', total: true, ancho: 10,
      valor: r => new Set(
        (porPedido.get(r.salesid) ?? []).map(l => l.factura?.trim()).filter(Boolean),
      ).size,
    },
    {
      titulo: x('loadCount'), tipo: 'entero', total: true, ancho: 10,
      valor: r => cargasPorPedido.get(r.salesid)?.size ?? 0,
    },
  ], pedidos, x('total'))

  // ── Hoja 2: todas las líneas ──────────────────────────────────────────────
  const colsLinea: Col<PedidoVentaLinea>[] = [
    ...colEmpresa<PedidoVentaLinea>(l => l.dataareaid),
    { titulo: x('orderId'), ancho: 18, valor: l => l.numPedido },
    colCliente<PedidoVentaLinea>(l => l.numPedido),
    { titulo: x('lineNum'), tipo: 'entero', ancho: 8, valor: l => l.numLinea ?? null },
    { titulo: x('item'), ancho: 18, valor: l => fmtArt(l.articulo) || null },
    { titulo: x('description'), ancho: 34, valor: l => l.descripcion ?? null },
    { titulo: x('family'), ancho: 18, valor: l => l.familia ?? null },
    { titulo: x('subfamily'), ancho: 18, valor: l => l.subfamilia ?? null },
    { titulo: x('qtyLine'), tipo: 'cantidad', total: true, valor: l => l.qtyLinea },
    { titulo: x('qtyOrdered'), tipo: 'cantidad', total: true, valor: l => l.qtyPedida },
    { titulo: x('qtyServed'), tipo: 'cantidad', total: true, valor: l => l.qtyServida },
    { titulo: x('qtyUnbilled'), tipo: 'cantidad', total: true, valor: l => l.qtySinFacturar },
    { titulo: x('um'), ancho: 8, valor: l => l.um ?? null },
    { titulo: x('unitPrice'), tipo: 'importe', valor: l => l.precioUnitario || null },
    { titulo: x('netAmount'), tipo: 'importe', total: true, ancho: 15, valor: l => l.importeNeto },
    { titulo: x('pendingAmount'), tipo: 'importe', total: true, ancho: 15, valor: l => l.importePendiente },
    { titulo: x('currency'), ancho: 9, valor: l => l.moneda ?? 'EUR' },
    { titulo: x('lineStatus'), ancho: 16, valor: l => estado(l.estadoLinea) },
    { titulo: x('invoice'), ancho: 16, valor: l => l.factura?.trim() || null },
    { titulo: x('packingSlip'), ancho: 18, valor: albaranDeFila },
    { titulo: x('load'), ancho: 18, valor: cargaDeFila },
    { titulo: x('destination'), ancho: 16, valor: l => refDeFila(l, 'destino') },
    { titulo: x('customerRefLoad'), ancho: 14, valor: l => refDeFila(l, 'referencia') },
    { titulo: x('deliveryDate'), tipo: 'fecha', valor: l => fecha(l.fechaEntrega) },
    { titulo: x('shipDate'), tipo: 'fecha', valor: l => fecha(l.fechaEnvio) },
  ]
  hoja(wb, x('sheetLines'), colsLinea, lineas, x('total'))

  // ── Hoja 3: lo facturado, factura a factura ───────────────────────────────
  const facturadas = lineas.filter(l => !!l.factura?.trim())
  hoja<PedidoVentaLinea>(wb, x('sheetInvoices'), [
    ...colEmpresa<PedidoVentaLinea>(l => l.dataareaid),
    { titulo: x('orderId'), ancho: 18, valor: l => l.numPedido },
    colCliente<PedidoVentaLinea>(l => l.numPedido),
    { titulo: x('invoice'), ancho: 18, valor: l => l.factura?.trim() ?? null },
    { titulo: x('invoiceDate'), tipo: 'fecha', valor: l => fecha(l.primeraFechaFactura) },
    { titulo: x('packingSlip'), ancho: 18, valor: albaranDeFila },
    { titulo: x('load'), ancho: 18, valor: cargaDeFila },
    { titulo: x('destination'), ancho: 16, valor: l => refDeFila(l, 'destino') },
    { titulo: x('customerRefLoad'), ancho: 14, valor: l => refDeFila(l, 'referencia') },
    { titulo: x('lineNum'), tipo: 'entero', ancho: 8, valor: l => l.numLinea ?? null },
    { titulo: x('item'), ancho: 18, valor: l => fmtArt(l.articulo) || null },
    { titulo: x('description'), ancho: 34, valor: l => l.descripcion ?? null },
    { titulo: x('billedQty'), tipo: 'cantidad', total: true, valor: l => l.qtyFacturada },
    { titulo: x('billedAmount'), tipo: 'importe', total: true, ancho: 16, valor: l => l.importeFacturado },
    { titulo: x('currency'), ancho: 9, valor: l => l.moneda ?? 'EUR' },
  ], facturadas, x('total'))

  // ── Hoja 4: lo que queda pendiente ────────────────────────────────────────
  //
  // Las líneas sin factura, con las dos cifras que la pantalla enseña en su
  // banda ámbar: lo que falta por facturar y lo que ni siquiera se ha lanzado a
  // una carga. La segunda no existe en origen; sale de cruzar con las cargas.
  const pendientes = lineas.filter(l => !l.factura?.trim())
  hoja<PedidoVentaLinea>(wb, x('sheetPending'), [
    ...colEmpresa<PedidoVentaLinea>(l => l.dataareaid),
    { titulo: x('orderId'), ancho: 18, valor: l => l.numPedido },
    colCliente<PedidoVentaLinea>(l => l.numPedido),
    { titulo: x('lineNum'), tipo: 'entero', ancho: 8, valor: l => l.numLinea ?? null },
    { titulo: x('item'), ancho: 18, valor: l => fmtArt(l.articulo) || null },
    { titulo: x('description'), ancho: 34, valor: l => l.descripcion ?? null },
    { titulo: x('qtyLine'), tipo: 'cantidad', total: true, valor: l => l.qtyLinea },
    { titulo: x('qtyOrdered'), tipo: 'cantidad', total: true, valor: l => l.qtyPedida },
    { titulo: x('qtyUnbilled'), tipo: 'cantidad', total: true, valor: l => l.qtySinFacturar },
    {
      titulo: x('qtyLaunched'), tipo: 'cantidad', total: true,
      valor: l => (l.inventtransid ? pickingPorTransid.get(l.inventtransid)?.solicitada : 0) ?? 0,
    },
    { titulo: x('qtyToLaunch'), tipo: 'cantidad', total: true, valor: sinLanzar },
    { titulo: x('packingSlip'), ancho: 18, valor: albaranDeFila },
    { titulo: x('pendingAmount'), tipo: 'importe', total: true, ancho: 15, valor: l => l.importePendiente },
    { titulo: x('currency'), ancho: 9, valor: l => l.moneda ?? 'EUR' },
    { titulo: x('deliveryDate'), tipo: 'fecha', valor: l => fecha(l.fechaEntrega) },
  ], pendientes, x('total'))

  // ── Hoja 5: preparación de cargas ─────────────────────────────────────────
  hoja<CargaLinea>(wb, x('sheetLoads'), [
    ...colEmpresa<CargaLinea>(c => c.empresa),
    { titulo: x('orderId'), ancho: 18, valor: c => c.numPedido },
    colCliente<CargaLinea>(c => c.numPedido),
    { titulo: x('load'), ancho: 18, valor: c => c.carga },
    { titulo: x('loadCustomer'), ancho: 18, valor: c => c.referenciaCliente ?? null },
    { titulo: x('customerRef'), ancho: 18, valor: c => c.referenciaCliente2 ?? null },
    {
      titulo: x('palletised'), ancho: 12,
      valor: c => (c.paletizada == null
        ? null
        : String(t(c.paletizada
            ? 'salesOrders.picking.paletizada'
            : 'salesOrders.picking.noPaletizada'))),
    },
    {
      titulo: x('lineNum'), tipo: 'entero', ancho: 8,
      valor: c => lineaPorTransid.get(c.transid)?.numLinea ?? null,
    },
    { titulo: x('item'), ancho: 18, valor: c => fmtArt(c.articulo) || null },
    {
      titulo: x('description'), ancho: 34,
      valor: c => lineaPorTransid.get(c.transid)?.descripcion ?? null,
    },
    { titulo: x('color'), ancho: 12, valor: c => c.color ?? null },
    { titulo: x('style'), ancho: 12, valor: c => c.estilo ?? null },
    { titulo: x('qtyRequested'), tipo: 'cantidad', total: true, valor: c => c.qtySolicitada },
    // Entre la solicitada y la preparada, igual que en la pantalla. Aquí el 0 sí
    // se escribe —y no se deja la celda vacía como en los pesos— porque un cero
    // reservado es una medida: significa que no hay nada atado.
    { titulo: x('qtyBooked'), tipo: 'cantidad', total: true, valor: c => c.qtyReservada },
    { titulo: x('qtyPicked'), tipo: 'cantidad', total: true, valor: c => c.qtyPicking },
    { titulo: x('qtyToPick'), tipo: 'cantidad', total: true, valor: c => c.qtyPendiente },
    // Bultos y peso, sobre lo SOLICITADO en la carga. El `?? null` no es
    // cosmética: deja la celda VACÍA cuando el maestro no tiene el peso, y así
    // el total de la columna suma sólo lo que existe. Un 0 aquí se sumaría igual
    // pero convertiría un hueco en una medida, y en un Excel que alguien reenvía
    // eso ya no se puede deshacer.
    { titulo: x('boxes'), tipo: 'entero', total: true, valor: c => c.cajas ?? null },
    { titulo: x('netWeight'), tipo: 'cantidad', total: true, valor: c => c.pesoNeto ?? null },
    // El «(sin palet)» viaja en la cabecera de la columna: el fichero se lee
    // fuera de la aplicación, donde no hay tooltip que valga.
    { titulo: x('grossWeight'), tipo: 'cantidad', total: true, valor: c => c.pesoBruto ?? null },
    {
      titulo: x('pickingStatus'), ancho: 16,
      valor: c => String(t(
        `salesOrders.picking.estado.${estadoDe(c.qtySolicitada, c.qtyPicking)}`,
      )),
    },
    {
      // Dos estados distintos y los dos importan: cómo va la preparación y si la
      // mercancía sigue en casa.
      titulo: x('loadState'), ancho: 14,
      valor: c => String(t(c.expedida
        ? 'salesOrders.picking.expedida'
        : 'salesOrders.picking.enAlmacen')),
    },
  ], cargas, x('total'))

  return wb
}

/** Construye y descarga el libro. */
export async function exportPedidosVentasExcel(
  datos: DatosExportVentas,
  opciones: Opciones,
): Promise<void> {
  const generado = opciones.generado ?? new Date()
  const wb = buildPedidosVentasWorkbook(datos, { ...opciones, generado })
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${String(opciones.t('salesOrders.excel.filename'))}_${
    generado.toISOString().slice(0, 10)}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}
